import type { MetricChartSpec } from '@/components/charts/generic/types';
import { graphSettings } from '@/config/settings';
import type { HealthWoundTrendPoint } from '@/domain/health';
import { formatDateByPattern } from '@/utils/date';
import { getChartLabelDateFormat, type Locale } from '@/utils/localeFormatters';

import { extractWoundTrendDayLabels } from './healthWoundsGraphHelpers';

const FISH_COUNT_COLOR = graphSettings.graphColors.fishCount;
const FISH_COUNT_FILL_OPACITY = 0.8;

const TARGET_Y_TICK_COUNT = 5;
/** Date labels under the bars, as many as fit the card without crowding. */
const MAX_X_TICKS = 8;

const NICE_Y_STEPS = [50, 100, 250, 500, 1000, 2000, 2500, 5000] as const;

function roundUpToNiceStep(roughStep: number): number {
    for (const step of NICE_Y_STEPS) {
        if (step >= roughStep) {
            return step;
        }
    }
    return Math.ceil(roughStep / 500) * 500;
}

export function computeFishCountYAxisScale(maxCount: number): { yMax: number; stepSize: number } {
    if (maxCount <= 0) {
        return { yMax: 500, stepSize: 100 };
    }

    const roughStep = maxCount / TARGET_Y_TICK_COUNT;
    const stepSize = roundUpToNiceStep(roughStep);
    const yMax = Math.ceil(maxCount / stepSize) * stepSize;

    return { yMax, stepSize };
}

/**
 * The fish counted per day under the wound graphs, as a MetricChart bar spec: whole counts on
 * a 0-based axis with a few round steps, the date in the tooltip title, and the count under
 * the "measurements" label.
 */
export function buildWoundFishCountChartSpec(
    points: HealthWoundTrendPoint[],
    locale: Locale,
    measurementsLabel: string
): MetricChartSpec<'bar'> {
    const counts = points
        .map((point) => point.woundCount)
        .filter((value): value is number => value != null && !Number.isNaN(value));
    const maxCount = counts.length > 0 ? Math.max(...counts) : 0;
    const { yMax, stepSize } = computeFishCountYAxisScale(maxCount);
    const formatPattern = getChartLabelDateFormat(locale);

    return {
        kind: 'bar',
        days: extractWoundTrendDayLabels(points),
        series: [
            {
                key: 'fishCount',
                label: measurementsLabel,
                color: FISH_COUNT_COLOR,
                barOpacity: FISH_COUNT_FILL_OPACITY,
                barBorder: { color: FISH_COUNT_COLOR, width: 1 },
                showInLegend: false,
                data: points.map((point) => ({ day: point.day, value: point.woundCount ?? null })),
            },
        ],
        left: {
            label: '',
            min: 0,
            max: yMax,
            stepSize,
            maxTicksLimit: TARGET_Y_TICK_COUNT + 1,
            tickDecimals: 0,
            valueDecimals: 0,
        },
        xTicks: { maxTicksLimit: MAX_X_TICKS },
        locale,
        // A day without a count has no bar, so it has nothing to say in the tooltip either.
        tooltipSkipEmpty: true,
        tooltipTitle: (day) => formatDateByPattern(day, formatPattern, locale, ''),
    };
}
